import React, { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useFeatureFlags } from "../../lib/featureFlags";
import { Spinner, btnGhost, btnPrimary, inputCls, labelCls } from "./shared";

const validUrl = (u) => !u || /^https?:\/\//i.test(u);

function CtaEditor({ cta, onChange, readOnly, showUpgrade }) {
  const buttons = cta.buttons || [];
  const setBtn = (id, patch) => onChange({ ...cta, buttons: buttons.map((b) => (b.id === id ? { ...b, ...patch } : b)) });
  return (
    <div className="space-y-4">
      <div><label className={labelCls}>Headline</label>
        <input className={inputCls} disabled={readOnly} value={cta.headline || ""} onChange={(e) => onChange({ ...cta, headline: e.target.value })} placeholder="Unlock Full Video" /></div>
      <div><label className={labelCls}>Supporting text</label>
        <textarea rows={3} className={`${inputCls} resize-y`} disabled={readOnly} value={cta.text || ""} onChange={(e) => onChange({ ...cta, text: e.target.value })} /></div>
      <div className="space-y-2">
        <div className="flex items-center justify-between"><span className={labelCls}>Buttons / links</span>
          {!readOnly && <button type="button" className="text-xs text-purple-300 hover:text-white" onClick={() => onChange({ ...cta, buttons: [...buttons, { id: `c${Date.now().toString(36)}`, kind: "link", label: "Custom link", url: "", visible: true }] })}><Plus size={12} className="inline" /> Add link</button>}
        </div>
        {buttons.filter((b) => showUpgrade || b.kind !== "upgrade").map((b) => (
          <div key={b.id} className={`grid grid-cols-12 gap-2 items-center rounded-lg border p-2 ${b.visible ? "border-zinc-800" : "border-zinc-900 opacity-60"}`}>
            <input className={`${inputCls} col-span-12 sm:col-span-3`} aria-label="Button label" disabled={readOnly} value={b.label} onChange={(e) => setBtn(b.id, { label: e.target.value })} />
            <input className={`${inputCls} col-span-9 sm:col-span-7 ${validUrl(b.url) ? "" : "border-red-500"}`} aria-label="Button URL" disabled={readOnly} value={b.url} onChange={(e) => setBtn(b.id, { url: e.target.value })} placeholder="https://" />
            <label className="col-span-2 sm:col-span-1 flex items-center gap-1 text-xs text-zinc-300" title="Show / hide">
              <input type="checkbox" className="accent-purple-500" disabled={readOnly} checked={b.visible} onChange={(e) => setBtn(b.id, { visible: e.target.checked })} /> Show
            </label>
            {!readOnly && b.id !== "upgrade" && <button type="button" aria-label="Remove link" className="col-span-1 p-1.5 rounded hover:bg-red-900/40 text-zinc-500 hover:text-red-400" onClick={() => onChange({ ...cta, buttons: buttons.filter((x) => x.id !== b.id) })}><Trash2 size={14} /></button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CtaTab({ record, setField, allowGlobalEdit = true }) {
  const flags = useFeatureFlags();
  const [globalCta, setGlobalCta] = useState(null);
  const [editGlobal, setEditGlobal] = useState(false);
  const [savingGlobal, setSavingGlobal] = useState(false);

  useEffect(() => {
    api.get("/api/video-central/config/default-cta").then((r) => setGlobalCta(r.data)).catch(() => toast.error("Could not load the global CTA"));
  }, []);

  if (!globalCta) return <Spinner label="Loading CTA..." />;
  const cta = record.cta || {};
  const useDefault = cta.use_default !== false;

  const setOverride = (next) => {
    if ((next.buttons || []).some((b) => !validUrl(b.url))) toast.error("Links must start with http:// or https://");
    setField("cta", { ...next, use_default: false });
  };
  const toggleDefault = (checked) => {
    if (checked) setField("cta", { ...cta, use_default: true });
    else setField("cta", { ...JSON.parse(JSON.stringify(globalCta)), use_default: false });
  };
  const saveGlobal = async () => {
    if ((globalCta.buttons || []).some((b) => !validUrl(b.url))) return toast.error("Links must start with http:// or https://");
    setSavingGlobal(true);
    try {
      const r = await api.put("/api/video-central/config/default-cta", { cta: globalCta });
      setGlobalCta(r.data);
      toast.success("Global default CTA saved");
      setEditGlobal(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the global CTA");
    } finally { setSavingGlobal(false); }
  };

  return (
    <div className="space-y-5">
      <label className="flex items-center gap-2 text-sm text-zinc-200">
        <input type="checkbox" className="accent-purple-500" checked={useDefault} onChange={(e) => toggleDefault(e.target.checked)} />
        Use global default CTA for this video
      </label>

      {useDefault ? (
        <>
          <CtaEditor cta={globalCta} readOnly={!editGlobal} onChange={setGlobalCta} showUpgrade={flags.ENABLE_PAID_TIERS} />
          <div className="flex gap-2">
            {!editGlobal ? <button type="button" disabled={!allowGlobalEdit} className={btnGhost} onClick={() => setEditGlobal(true)}>Edit global default</button> : (
              <><button type="button" className={btnPrimary} disabled={savingGlobal} onClick={saveGlobal}>{savingGlobal ? "Saving..." : "Save global default"}</button>
                <button type="button" className={btnGhost} onClick={() => { setEditGlobal(false); api.get("/api/video-central/config/default-cta").then((r) => setGlobalCta(r.data)); }}>Cancel</button></>
            )}
          </div>
          {editGlobal && <p className="text-xs text-amber-300">Global changes apply to every video that uses the default CTA.</p>}
        </>
      ) : (
        <CtaEditor cta={cta} onChange={setOverride} showUpgrade={flags.ENABLE_PAID_TIERS} />
      )}
      <p className="text-xs text-zinc-500">Links here are shared with the Catalog and the Promo Pack Generator.</p>
    </div>
  );
}
